import { useEffect, useState } from "react";
import type { RendererModeInput, RendererParams } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useEditorStore } from "../../../../state/editor/useEditor";
import { useCaps } from "../../CapsProvider";
import { CompactNumber, CompactSelect, CompactSlider, Row } from "../../inspector/rows";

/** Renderer output controls (v13): render scale, explicit size override, mode.
 * Seeds from getRendererParams; each change calls setRendererParams and stores
 * the canonical returned value. */
export function RendererControl() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const store = useEditorStore();
  const [p, setP] = useState<RendererParams | null>(null);

  useEffect(() => {
    r.getRendererParams().then(setP).catch(() => {});
  }, [r]);

  if (!p) return null;
  const set = (patch: Partial<Omit<RendererParams, "mode">> & { mode?: RendererModeInput }) =>
    r.setRendererParams(patch).then((next) => {
      setP(next);
      // Publish it: the export panels default their size from this, and until
      // now the value lived only in this component's local state.
      void store.refresh("renderer");
    });

  // v31 renamed the wire spelling "preview" -> "custom" (canonical; "preview"
  // is still accepted as a deprecated input alias). Send the legacy spelling
  // to a pre-v31 viewer, which only branches on the literal string "preview".
  const customMode: RendererModeInput = caps.v31 ? "custom" : "preview";
  const modeValue = (p.mode as string) === "preview" ? customMode : p.mode;

  return (
    <>
      <Row label="Render Scale">
        <CompactSlider value={p.renderScale} min={0.25} max={5} step={0.05} onChange={(v) => void set({ renderScale: v })} />
      </Row>
      <Row label="Width (0=auto)">
        <CompactNumber value={p.width} min={0} step={1} onChange={(v) => void set({ width: v })} />
      </Row>
      <Row label="Height (0=auto)">
        <CompactNumber value={p.height} min={0} step={1} onChange={(v) => void set({ height: v })} />
      </Row>
      <Row label="Mode">
        <CompactSelect
          value={modeValue}
          options={[
            { id: "full", label: "Full" },
            // webgi calls this "Custom" (explicit width/height override).
            { id: customMode, label: "Custom" },
          ]}
          onChange={(v) => void set({ mode: v as RendererModeInput })}
        />
      </Row>
    </>
  );
}
